import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';

import {
  AdLabel,
  PlacementGradient,
  PlacementHeadline,
  PlacementFinalUrl,
  PlacementDescription,
  PlacementAction,
  BusinessNameWithAdsLabel,
} from '../../shared-components';

import { ResponsiveMMAPlacementCard } from './responsive-mma-placement-card';

export const ResponsiveAdPlacementMmaBase = ({
  placementData, // UAPVMSingleImagePlacement
  isEnabled,
  customClassName,
  imagePreviewHeight,

  showShortHeadline,
  shortHeadline,
  isMultiLineShortHeadline,

  showLongHeadline,
  longHeadline,
  isMultiLineLongHeadline,

  showFinalUrl,
  finalUrl,
  isMultiLineFinalUrl,
  displayUrl,

  showDescription,
  description,
  isMultiLineDescription,

  showCallToAction,
  callToAction,

  showAdLabel,
  showGradient,

  showBusinessName,
  businessName,
  showBusinessNameAdLabel,

  showLogo,
  logo,

  i18n, // may need to set window._TL_ in other platforms
  classes,
}) => {
  const {
    placementMeta, placementCropping, imageMeta, effectiveImageUrl, effectiveImageWidth, effectiveImageHeight, expandedPlacementCropping,
  } = placementData || {};
  const { previewHeight, aspectRatio, logoHeight } = placementMeta || {};
  if (!placementData || !imageMeta) {
    return null;
  }

  const finalImagePreviewHeight = imagePreviewHeight > 0 ? imagePreviewHeight : previewHeight;

  const cropping = expandedPlacementCropping || placementCropping;

  return (
    <ResponsiveMMAPlacementCard url={finalUrl} isEnabled={isEnabled} mmaPlacementCardClassName={classnames(classes.placementCard, customClassName)}>
      <div className={classnames(classes.placementImageContainer)}>
        {showAdLabel && <AdLabel i18n={i18n} adsLabelClassName={classes.placementAdsLabel} />}
        {showGradient && <PlacementGradient gradientClassName={classes.placementGradient} />}
        {
          imageMeta.url &&
            <CroppedImageView
              resizeImageHeight={finalImagePreviewHeight}
              aspectRatio={aspectRatio}
              src={effectiveImageUrl || imageMeta.url}
              originalWidth={effectiveImageWidth || imageMeta.originalWidth}
              originalHeight={effectiveImageHeight || imageMeta.originalHeight}
              SourceX={cropping && cropping.SourceX}
              SourceY={cropping && cropping.SourceY}
              SourceWidth={cropping && cropping.SourceWidth}
              SourceHeight={cropping && cropping.SourceHeight}
            />
          }
      </div>
      <div className={classnames(classes.placementTextContainer, 'text-container')}>
        {showLogo && logo &&
        <div className="logo-and-text-container">
          <div className={classnames(classes.placementLogoContainer, 'logo-container')}>
            <CroppedImageView
              resizeImageHeight={logoHeight || 24}
              src={logo.Src}
              originalWidth={logo.Width}
              originalHeight={logo.Height}
              SourceX={logo.CropSettings.SourceX}
              SourceY={logo.CropSettings.SourceY}
              SourceWidth={logo.CropSettings.SourceWidth}
              SourceHeight={logo.CropSettings.SourceHeight}
              aspectRatio={1}
            />
          </div>
          <div className="logo-text">
            <div className="logo-business-name">{businessName}</div>
            {showFinalUrl &&
            <PlacementFinalUrl
              finalUrl={finalUrl}
              displayUrl={displayUrl}
              isMultiLine={isMultiLineFinalUrl}
              placementFinalUrlClassName={classnames(classes.placementFinalUrl, 'final-url')}
            />
              }
          </div>
        </div>
        }
        {showShortHeadline && (
        <PlacementHeadline
          headlineText={shortHeadline}
          isMultiLine={isMultiLineShortHeadline}
          placementHeadlineClassName={classnames(classes.placementShortHeadline, 'short-headline')}
        />
        )}
        {showLongHeadline && (
        <PlacementHeadline
          headlineText={longHeadline}
          isMultiLine={isMultiLineLongHeadline}
          placementHeadlineClassName={classnames(classes.placementLongHeadline, 'long-headline')}
        />
        )}
        {(!showLogo || !logo) && showFinalUrl && (
        <PlacementFinalUrl
          finalUrl={finalUrl}
          displayUrl={displayUrl}
          isMultiLine={isMultiLineFinalUrl}
          placementFinalUrlClassName={classnames(classes.placementFinalUrl, 'final-url')}
        />
        )}
        {showDescription && (
        <PlacementDescription
          description={description}
          isMultiLine={isMultiLineDescription}
          placementDescriptionClassName={classnames(classes.placementDescription, 'description')}
        />
        )}
        {showBusinessName && (
        <BusinessNameWithAdsLabel
          showAdsLabel={showBusinessNameAdLabel}
          businessName={businessName}
          i18n={i18n}
          rootClassName={classnames(classes.placementBusinessNameWithAdsLabel, 'business-name-with-ads')}
        />
        )}
        {showCallToAction && callToAction && ( // add a space before each capital letter, e.g. "ActNow" => "Act Now"
        <PlacementAction actionText={callToAction.replace(/([A-Z])/g, ' $1')} placementActionClassName={classes.placementCallToAction} />
        )}
      </div>
    </ResponsiveMMAPlacementCard>
  );
};

ResponsiveAdPlacementMmaBase.propTypes = {
  placementData: PropTypes.shape({}).isRequired, // UAPVMSingleImagePlacement
  isEnabled: PropTypes.bool,
  customClassName: PropTypes.string,
  imagePreviewHeight: PropTypes.number,

  showShortHeadline: PropTypes.bool,
  shortHeadline: PropTypes.string,
  isMultiLineShortHeadline: PropTypes.bool,

  showLongHeadline: PropTypes.bool,
  longHeadline: PropTypes.string,
  isMultiLineLongHeadline: PropTypes.bool,

  showFinalUrl: PropTypes.bool,
  finalUrl: PropTypes.string,
  isMultiLineFinalUrl: PropTypes.bool,
  displayUrl: PropTypes.string,

  showDescription: PropTypes.bool,
  description: PropTypes.string,
  isMultiLineDescription: PropTypes.bool,

  showCallToAction: PropTypes.bool,
  callToAction: PropTypes.string,

  showAdLabel: PropTypes.bool,
  showGradient: PropTypes.bool,

  showBusinessName: PropTypes.bool,
  businessName: PropTypes.string,
  showBusinessNameAdLabel: PropTypes.bool,

  showLogo: PropTypes.bool,
  logo: PropTypes.shape({}),

  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    placementCard: PropTypes.string,
    placementImageContainer: PropTypes.string,
    placementTextContainer: PropTypes.string,
    placementAdsLabel: PropTypes.string,
    placementGradient: PropTypes.string,
    placementShortHeadline: PropTypes.string,
    placementLongHeadline: PropTypes.string,
    placementFinalUrl: PropTypes.string,
    placementDescription: PropTypes.string,
    placementCallToAction: PropTypes.string,
    placementBusinessNameWithAdsLabel: PropTypes.string,
  }).isRequired,
};

ResponsiveAdPlacementMmaBase.defaultProps = {
  isEnabled: true,
  customClassName: '',
  imagePreviewHeight: -1,

  showShortHeadline: false,
  showLongHeadline: false,
  showFinalUrl: false,
  showDescription: false,
  showCallToAction: false,
  showAdLabel: false,
  showGradient: false,
  showBusinessName: false,
  showBusinessNameAdLabel: false,
  showLogo: false,

  shortHeadline: undefined,
  isMultiLineShortHeadline: false,
  longHeadline: undefined,
  isMultiLineLongHeadline: false,
  finalUrl: undefined,
  displayUrl: undefined,
  isMultiLineFinalUrl: false,
  description: undefined,
  isMultiLineDescription: false,
  callToAction: undefined,
  businessName: undefined,
  logo: undefined,
};
